import React, { useState } from "react";
import { Button, Spin } from "antd";
import { PlusOutlined, HolderOutlined, LoadingOutlined } from "@ant-design/icons";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableSectionItem = ({ section, index, isActive, onClick, showDragIcon, isDeleting }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: section.id });
    
    const [isHovered, setIsHovered] = useState(false);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div
            ref={setNodeRef}
            style={{
                ...style,
                padding: '8px 12px',
                marginBottom: '8px',
                background: isDeleting ? '#ffccc7' : (isDragging ? '#d1e7ff' : (isActive ? '#0a497b' : '#f7f7f7')),
                color: isDeleting ? '#cf1322' : (isDragging ? '#000' : (isActive ? '#fff' : 'black')),
                borderRadius: '4px',
                cursor: isDeleting ? 'not-allowed' : 'pointer',
                boxShadow: isDragging ? '0 6px 16px rgba(0,0,0,0.25)' : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                opacity: isDeleting ? 0.7 : (isDragging ? 0.9 : 1),
                pointerEvents: isDeleting ? 'none' : 'auto',
            }}
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <span style={{ flex: 1 }}>{isDeleting ? 'Deleting...' : section.title}</span>
            {isDeleting ? (
                <Spin indicator={<LoadingOutlined style={{ fontSize: 14 }} spin />} />
            ) : (
                showDragIcon && (
                    <div
                        {...attributes}
                        {...listeners}
                        style={{
                            cursor: isDragging ? 'grabbing' : 'move',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '4px'
                        }}
                    >
                        <HolderOutlined style={{ 
                            fontSize: '18px', 
                            opacity: 1,
                            fontWeight: 'bold'
                        }} />
                    </div>
                )
            )}
        </div>
    );
};

const ReportSidebar = ({
    sections,
    activeSectionIndex,
    onSectionClick,
    onAddSection,
    addingSection,
    showConclusion,
    onReorderSections,
    deletingSectionIndex
}) => {
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = sections.findIndex(s => s.id === active.id);
            const newIndex = sections.findIndex(s => s.id === over.id);
            
            if (onReorderSections) {
                onReorderSections(oldIndex, newIndex);
            }
        }
    };
    return (
        <div className="photoreport-section-seidebar" style={{ position: 'sticky', top: '20px' }}>
            <h4>Sections</h4>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                    {sections.map((section, index) => (
                        <SortableSectionItem
                            key={section.id}
                            section={section}
                            index={index}
                            isActive={activeSectionIndex === index}
                            onClick={() => onSectionClick(index)}
                            showDragIcon={sections.length > 1}
                            isDeleting={deletingSectionIndex === index}
                        />
                    ))}
                </SortableContext>
            </DndContext>
            {showConclusion && (
                <div
                    onClick={() => onSectionClick(sections.length)}
                    style={{
                        padding: '8px 12px',
                        marginBottom: '8px',
                        background: activeSectionIndex === sections.length ? '#0a497b' : '#1890ff',
                        color: activeSectionIndex === sections.length ? '#fff' : '#fff',
                        borderRadius: '30px',
                        border: '1px solid',
                        textAlign: 'center',
                        cursor: 'pointer'
                    }}
                >
                    Conclusion
                </div>
            )}
            <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={onAddSection}
                loading={addingSection}
                disabled={addingSection}
                style={{ width: '100%', marginTop: '8px' }}
            >
                {addingSection ? 'Adding...' : 'New Section'}
            </Button>
        </div>
    );
};

export default ReportSidebar;


